import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        setUser(data);
        const r = data.roles || [];
        const staff = r.some((x) => ["RM", "Manager", "Admin"].includes(x));
        const home = r.includes("Partner") && !staff ? "/partner" : "/cases";
        window.history.replaceState(null, "", home);
        navigate(home, { state: { user: data } });
      } catch (e) {
        const status = e?.response?.status;
        setError(e?.response?.data?.detail || (status ? `Sign-in failed (${status}).` : `Couldn't reach the server from ${window.location.origin} — the request was blocked before it got to RM Desk. Open the app from its usual address and try again.`));
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
      {error ? (
        <div className="max-w-md text-center px-6">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <p className="text-sm text-slate-300" data-testid="auth-error">{error}</p>
          <button onClick={() => navigate("/")} className="mt-6 text-sm underline text-blue-300">
            Back to sign in
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin" /> Signing you in…
        </div>
      )}
    </div>
  );
}
